/**
 * Security Monitoring and Metrics
 *
 * Tracks authentication security events for monitoring and debugging:
 * - Failed login attempts
 * - Token refresh failures
 * - Circuit breaker trips
 * - Suspicious patterns
 *
 * In production, these metrics can be sent to monitoring services
 * (Sentry, Datadog, CloudWatch, etc.)
 */

import { isProduction } from '@/config/environment';

interface SecurityEvent {
  type: SecurityEventType;
  timestamp: number;
  data?: unknown;
  userAgent?: string;
  pathname?: string;
}

type SecurityEventType =
  | 'FAILED_LOGIN'
  | 'FAILED_TOKEN_REFRESH'
  | 'CIRCUIT_BREAKER_TRIP'
  | 'TOKEN_EXPIRED_ON_MOUNT'
  | 'INVALID_TOKEN_FORMAT'
  | 'CROSS_TAB_LOGOUT'
  | 'SUSPICIOUS_PATTERN';

class SecurityMonitor {
  private events: SecurityEvent[] = [];
  private maxEvents: number = 100; // Keep last 100 events in memory

  /**
   * Log a security event
   */
  logEvent(type: SecurityEventType, data?: unknown) {
    const event: SecurityEvent = {
      type,
      timestamp: Date.now(),
      data,
      userAgent: navigator.userAgent,
      pathname: window.location.pathname,
    };

    // Add to in-memory buffer
    this.events.push(event);

    // Keep only last maxEvents
    if (this.events.length > this.maxEvents) {
      this.events.shift();
    }

    // Console logging based on environment
    if (!isProduction) {
      console.warn(`🔒 Security Event: ${type}`, data);
    }

    // In production, send to monitoring service
    if (isProduction) {
      this.sendToMonitoringService(event);
    }

    // Check for suspicious patterns
    this.checkSuspiciousPatterns();
  }

  /**
   * Send event to monitoring service (placeholder)
   * In production, implement with Sentry, Datadog, etc.
   */
  private sendToMonitoringService(_event: SecurityEvent) {
    // Placeholder - implement with your monitoring service
    // Example with Sentry:
    // Sentry.captureMessage(`Security Event: ${event.type}`, {
    //   level: 'warning',
    //   extra: event,
    // });
    // Example with custom endpoint:
    // fetch('/api/security/log', {
    //   method: 'POST',
    //   headers: { 'Content-Type': 'application/json' },
    //   body: JSON.stringify(event),
    // }).catch(() => {}); // Fire and forget
  }

  /**
   * Check for suspicious patterns in recent events
   */
  private checkSuspiciousPatterns() {
    const recentEvents = this.getRecentEvents(60000); // Last minute

    // Pattern 1: Multiple failed logins (potential brute force)
    const failedLogins = recentEvents.filter(e => e.type === 'FAILED_LOGIN');
    if (failedLogins.length >= 5) {
      this.logEvent('SUSPICIOUS_PATTERN', {
        pattern: 'multiple_failed_logins',
        count: failedLogins.length,
      });
    }

    // Pattern 2: Multiple token refresh failures (potential token theft)
    const failedRefreshes = recentEvents.filter(e => e.type === 'FAILED_TOKEN_REFRESH');
    if (failedRefreshes.length >= 3) {
      this.logEvent('SUSPICIOUS_PATTERN', {
        pattern: 'multiple_failed_refreshes',
        count: failedRefreshes.length,
      });
    }

    // Pattern 3: Multiple circuit breaker trips (potential attack)
    const circuitBreakerTrips = recentEvents.filter(e => e.type === 'CIRCUIT_BREAKER_TRIP');
    if (circuitBreakerTrips.length >= 2) {
      this.logEvent('SUSPICIOUS_PATTERN', {
        pattern: 'multiple_circuit_breaker_trips',
        count: circuitBreakerTrips.length,
      });
    }
  }

  /**
   * Get events from the last N milliseconds
   */
  private getRecentEvents(timeWindowMs: number): SecurityEvent[] {
    const cutoff = Date.now() - timeWindowMs;
    return this.events.filter(e => e.timestamp >= cutoff);
  }

  /**
   * Get all events (for debugging)
   */
  getAllEvents(): SecurityEvent[] {
    return [...this.events];
  }

  /**
   * Get metrics summary
   */
  getMetrics(): {
    totalEvents: number;
    eventsByType: Record<SecurityEventType, number>;
    recentEvents: number;
  } {
    const eventsByType = this.events.reduce(
      (acc, event) => {
        acc[event.type] = (acc[event.type] || 0) + 1;
        return acc;
      },
      {} as Record<SecurityEventType, number>,
    );

    return {
      totalEvents: this.events.length,
      eventsByType,
      recentEvents: this.getRecentEvents(300000).length, // Last 5 minutes
    };
  }

  /**
   * Clear all events (for testing)
   */
  clear() {
    this.events = [];
  }

  /**
   * Export events as JSON (for debugging/reporting)
   */
  exportEvents(): string {
    return JSON.stringify(this.events, null, 2);
  }
}

// Singleton instance
export const securityMonitor = new SecurityMonitor();

/**
 * Convenience functions for logging common security events
 */

export function logFailedLogin(error?: string) {
  securityMonitor.logEvent('FAILED_LOGIN', { error });
}

export function logFailedTokenRefresh(status?: number, error?: string) {
  securityMonitor.logEvent('FAILED_TOKEN_REFRESH', { status, error });
}

export function logCircuitBreakerTrip(failureCount: number) {
  securityMonitor.logEvent('CIRCUIT_BREAKER_TRIP', { failureCount });
}

export function logTokenExpiredOnMount() {
  securityMonitor.logEvent('TOKEN_EXPIRED_ON_MOUNT');
}

export function logInvalidTokenFormat(reason?: string) {
  securityMonitor.logEvent('INVALID_TOKEN_FORMAT', { reason });
}

export function logCrossTabLogout() {
  securityMonitor.logEvent('CROSS_TAB_LOGOUT');
}
